import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Plus, Edit2, Trash2, Star, CheckCircle2, AlertTriangle, MessageCircle, CreditCard, X, Save, QrCode } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import ImageUpload from './ImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const cls = 'w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm';

const EMPTY_FORM = { label: '', bank_name: '', account_number: '', account_holder: '', is_primary: false };

function AccountForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bank_name.trim() || !form.account_number.trim() || !form.account_holder.trim()) {
      toast.error('Nama bank, nomor rekening, dan nama pemilik wajib diisi');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-stone-50 rounded-xl p-4 space-y-3 border border-stone-200">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Label (opsional)</label>
          <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            className={cls} placeholder="mis. Rekening Operasional" />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Nama Bank *</label>
          <input value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
            className={cls} placeholder="mis. BSI / BNI / Mandiri" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Nomor Rekening *</label>
          <input value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))}
            className={cls} placeholder="mis. 1234567890" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Atas Nama *</label>
          <input value={form.account_holder} onChange={e => setForm(f => ({ ...f, account_holder: e.target.value }))}
            className={cls} placeholder="mis. Masjid Raya Batam" required />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.is_primary} onChange={e => setForm(f => ({ ...f, is_primary: e.target.checked }))}
          className="accent-emerald-600 w-4 h-4" />
        <span className="text-stone-600">Jadikan Rekening Utama</span>
      </label>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel}
          className="flex-1 py-2 rounded-xl border border-stone-200 text-sm text-stone-600 hover:bg-stone-50">
          Batal
        </button>
        <button type="submit" disabled={saving}
          className="flex-1 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2">
          <Save className="w-3.5 h-3.5" /> {saving ? 'Menyimpan...' : 'Simpan'}
        </button>
      </div>
    </form>
  );
}

const RekeningManager = () => {
  const { user } = useAuth();
  const { settings, updateSettings } = useSettings();
  const [accounts, setAccounts] = useState([]);
  const [platform, setPlatform] = useState(null);
  const [mosque, setMosque] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // Donasi digital settings
  const [waNumber, setWaNumber] = useState(settings.whatsapp_number || '');
  const [qrisUrl, setQrisUrl] = useState(settings.qris_image_url || '');
  const [savingDigital, setSavingDigital] = useState(false);

  useEffect(() => {
    loadAccounts();
    axios.get(`${API_URL}/api/platform-settings`).then(r => setPlatform(r.data)).catch(() => {});
    if (user?.mosque_id) {
      axios.get(`${API_URL}/api/mosques/${user.mosque_id}`).then(r => setMosque(r.data)).catch(() => {});
    }
  }, [user]);

  const loadAccounts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/mosque-accounts`, cfg);
      setAccounts(data);
    } catch {}
  };

  const handleAdd = async (form) => {
    try {
      await axios.post(`${API_URL}/api/mosque-accounts`, form, cfg);
      toast.success('Rekening berhasil ditambahkan');
      setShowAdd(false);
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menambahkan rekening');
    }
  };

  const handleEdit = async (form) => {
    try {
      await axios.put(`${API_URL}/api/mosque-accounts/${editId}`, form, cfg);
      toast.success('Rekening diperbarui');
      setEditId(null);
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memperbarui rekening');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus rekening ini?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API_URL}/api/mosque-accounts/${id}`, cfg);
      toast.success('Rekening dihapus');
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus rekening');
    } finally { setDeleting(null); }
  };

  const handleSetPrimary = async (id) => {
    try {
      await axios.put(`${API_URL}/api/mosque-accounts/${id}/set-primary`, {}, cfg);
      toast.success('Rekening utama diperbarui');
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengubah rekening utama');
    }
  };

  const saas = mosque?.saas_status || 'trial';
  const saasMap = {
    active: { label: 'Iuran Aktif', cls: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle2 },
    suspended: { label: 'Ditangguhkan', cls: 'bg-red-50 text-red-700', Icon: AlertTriangle },
    trial: { label: 'Masa Trial', cls: 'bg-amber-50 text-amber-700', Icon: AlertTriangle },
  };
  const saasInfo = saasMap[saas] || saasMap.trial;
  const SaasIcon = saasInfo.Icon;
  const rp = (n) => 'Rp ' + Math.round(n || 0).toLocaleString('id-ID');

  const handleSaveDigital = async () => {
    setSavingDigital(true);
    try {
      await updateSettings({ whatsapp_number: waNumber, qris_image_url: qrisUrl });
      toast.success('Pengaturan donasi digital disimpan!');
    } catch {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSavingDigital(false);
    }
  };

  const waLink = () => {
    const num = (platform?.saas_wa_number || '').replace(/\D/g, '');
    const msg = encodeURIComponent(`Assalamualaikum Admin masjidberdaya.id. Saya ${user?.name || ''} dari ${mosque?.name || 'masjid'} ingin konfirmasi pembayaran iuran sistem (SaaS).`);
    return `https://wa.me/${num}?text=${msg}`;
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="rekening-manager">

      {/* Daftar Rekening */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-emerald-700" />
            <h3 className="font-semibold text-stone-800">Rekening Bank Masjid</h3>
          </div>
          {!showAdd && (
            <button onClick={() => { setShowAdd(true); setEditId(null); }}
              data-testid="add-rekening-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full text-xs font-medium">
              <Plus className="w-3.5 h-3.5" /> Tambah Rekening
            </button>
          )}
        </div>
        <p className="text-xs text-stone-500">Rekening yang terdaftar dapat dipilih saat membuat Program Infaq.</p>

        {/* Form tambah baru */}
        {showAdd && (
          <AccountForm onSave={handleAdd} onCancel={() => setShowAdd(false)} />
        )}

        {/* Daftar Akun */}
        {accounts.length === 0 && !showAdd ? (
          <div className="text-center py-8 text-stone-400 text-sm">
            <Landmark className="w-10 h-10 mx-auto mb-2 opacity-30" />
            Belum ada rekening terdaftar. Tambahkan rekening pertama.
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map(acc => (
              <div key={acc.id} data-testid={`rekening-card-${acc.id}`}
                className={`rounded-xl border p-4 ${acc.is_primary ? 'border-emerald-300 bg-emerald-50' : 'border-stone-200 bg-white'}`}>
                {editId === acc.id ? (
                  <AccountForm
                    initial={{ label: acc.label, bank_name: acc.bank_name, account_number: acc.account_number, account_holder: acc.account_holder, is_primary: acc.is_primary }}
                    onSave={handleEdit}
                    onCancel={() => setEditId(null)}
                  />
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {acc.is_primary && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-600 text-white text-xs font-medium rounded-full">
                            <Star className="w-2.5 h-2.5" /> Utama
                          </span>
                        )}
                        <span className="text-xs font-semibold text-stone-600 truncate">
                          {acc.label && acc.label !== acc.bank_name ? acc.label : acc.bank_name}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-stone-800">{acc.bank_name} · {acc.account_number}</p>
                      <p className="text-xs text-stone-500">a/n {acc.account_holder}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!acc.is_primary && (
                        <button onClick={() => handleSetPrimary(acc.id)}
                          data-testid={`set-primary-${acc.id}`} title="Jadikan Rekening Utama"
                          className="p-1.5 rounded-lg text-stone-400 hover:text-amber-500 hover:bg-amber-50">
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => { setEditId(acc.id); setShowAdd(false); }}
                        data-testid={`edit-rekening-${acc.id}`}
                        className="p-1.5 rounded-lg text-stone-400 hover:text-emerald-600 hover:bg-emerald-50">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {!acc.is_primary && (
                        <button onClick={() => handleDelete(acc.id)} disabled={deleting === acc.id}
                          data-testid={`delete-rekening-${acc.id}`}
                          className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pengaturan Donasi Digital (WA + QRIS) */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-5" data-testid="digital-donation-settings">
        <div className="flex items-center gap-2">
          <QrCode className="w-5 h-5 text-emerald-700" />
          <h3 className="font-semibold text-stone-800">Pengaturan Donasi Digital</h3>
        </div>
        <p className="text-xs text-stone-500 -mt-3">Nomor WhatsApp dan QRIS akan tampil di formulir donasi jamaah.</p>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Nomor WhatsApp Konfirmasi Donasi
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">+</span>
            <input
              type="tel"
              data-testid="wa-number-input"
              placeholder="628123456789"
              value={waNumber}
              onChange={e => setWaNumber(e.target.value)}
              className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
            />
          </div>
          <p className="text-xs text-stone-400 mt-1">Masukkan dengan kode negara, contoh: 628123456789</p>
        </div>

        <div>
          <ImageUpload
            value={qrisUrl}
            onChange={url => setQrisUrl(url)}
            label="Foto QRIS Masjid"
          />
          <p className="text-xs text-stone-400 mt-1">Upload foto QRIS pembayaran untuk ditampilkan di halaman donasi.</p>
        </div>

        <button
          onClick={handleSaveDigital}
          disabled={savingDigital}
          data-testid="save-digital-btn"
          className="w-full py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <Save className="w-3.5 h-3.5" /> {savingDigital ? 'Menyimpan...' : 'Simpan Pengaturan Donasi'}
        </button>
      </div>

      {/* Iuran Sistem / SaaS Billing */}
      {platform && (
        <div className="bg-gradient-to-br from-emerald-900 to-emerald-950 rounded-2xl p-6 text-white" data-testid="saas-billing-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-400" />
              <h3 className="font-semibold text-amber-300">Iuran Sistem (SaaS)</h3>
            </div>
            <span className={`text-[11px] px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${saasInfo.cls}`} data-testid="saas-status-badge">
              <SaasIcon className="w-3.5 h-3.5" /> {saasInfo.label}
            </span>
          </div>
          <p className="text-2xl font-bold mb-1">{rp(platform.saas_monthly_fee)}<span className="text-sm text-white/60"> / bulan</span></p>
          <div className="bg-white/10 rounded-xl p-4 mt-4 space-y-1 text-sm">
            <p className="text-white/60 text-xs uppercase mb-1">Transfer ke:</p>
            <p><span className="text-white/60">Bank</span> · <span className="font-semibold">{platform.saas_bank_name}</span></p>
            <p><span className="text-white/60">No. Rekening</span> · <span className="font-semibold tracking-wide">{platform.saas_bank_account}</span></p>
            <p><span className="text-white/60">a/n</span> · <span className="font-semibold">{platform.saas_bank_holder}</span></p>
          </div>
          <a href={waLink()} target="_blank" rel="noreferrer" data-testid="saas-wa-confirm-btn"
            className="mt-4 w-full flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-semibold py-3 rounded-full transition-colors">
            <MessageCircle className="w-4 h-4" /> Konfirmasi Pembayaran via WhatsApp
          </a>
        </div>
      )}
    </div>
  );
};

export default RekeningManager;
